import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert,
} from '@mui/material'
import type { ClassSectionWithRelations, Teacher } from '@timetable/types'

interface SectionFormDialogProps {
  open: boolean
  section: ClassSectionWithRelations | null
  className: string
  teachers: Teacher[]
  existingSections: ClassSectionWithRelations[]
  allSections: ClassSectionWithRelations[]
  onClose: () => void
  onSubmit: (data: { section: string; class_teacher_id: string | null }) => void
}

export const SectionFormDialog: React.FC<SectionFormDialogProps> = ({
  open,
  section,
  className,
  teachers,
  existingSections,
  allSections,
  onClose,
  onSubmit,
}) => {
  const [sectionName, setSectionName] = React.useState('')
  const [classTeacherId, setClassTeacherId] = React.useState<string>('')
  const [error, setError] = React.useState<string | null>(null)

  // Build a map of teacher id -> assigned section label (excluding current section being edited)
  const assignedTeacherMap = React.useMemo(() => {
    const map = new Map<string, string>()
    allSections.forEach((s) => {
      if (s.class_teacher_id && s.id !== section?.id) {
        const classLabel = s.class?.class_name
          ? `${s.class.class_name}-${s.section}`
          : s.section
        map.set(s.class_teacher_id, classLabel)
      }
    })
    return map
  }, [allSections, section?.id])

  React.useEffect(() => {
    if (section) {
      setSectionName(section.section)
      setClassTeacherId(section.class_teacher_id || '')
    } else {
      setSectionName('')
      setClassTeacherId('')
    }
    setError(null)
  }, [section, open])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // Validate section name
    const trimmedSection = sectionName.trim()
    if (!trimmedSection) {
      setError('Section name is required')
      return
    }

    // Check for duplicate section names within the same class
    const isDuplicate = existingSections.some(
      (s) =>
        s.section.toLowerCase() === trimmedSection.toLowerCase() &&
        s.id !== section?.id
    )

    if (isDuplicate) {
      setError(`Section "${trimmedSection}" already exists for Class ${className}`)
      return
    }

    // Check if selected teacher is already a class teacher elsewhere
    if (classTeacherId && assignedTeacherMap.has(classTeacherId)) {
      const assignedTo = assignedTeacherMap.get(classTeacherId)
      const teacherName = teachers.find((t) => t.id === classTeacherId)?.name || 'This teacher'
      setError(`${teacherName} is already the class teacher of ${assignedTo}. A teacher can only be assigned to one section.`)
      return
    }

    onSubmit({
      section: trimmedSection,
      class_teacher_id: classTeacherId || null,
    })
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {section ? 'Edit Section' : `Add Section to Class ${className}`}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}

            <TextField
              label="Section Name"
              value={sectionName}
              onChange={(e) => setSectionName(e.target.value)}
              required
              fullWidth
              autoFocus
              placeholder="e.g., A, B, C"
              helperText="Enter a unique section identifier for this class"
            />

            <FormControl fullWidth>
              <InputLabel>Class Teacher (Optional)</InputLabel>
              <Select
                value={classTeacherId}
                onChange={(e) => setClassTeacherId(e.target.value)}
                label="Class Teacher (Optional)"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {teachers.map((teacher) => {
                  const assignedTo = assignedTeacherMap.get(teacher.id)
                  return (
                    <MenuItem
                      key={teacher.id}
                      value={teacher.id}
                      disabled={!!assignedTo}
                    >
                      {teacher.name}
                      {assignedTo && (
                        <Box component="span" sx={{ ml: 1, fontSize: '0.75rem', color: 'text.disabled' }}>
                          (class teacher of {assignedTo})
                        </Box>
                      )}
                    </MenuItem>
                  )
                })}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained">
            {section ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
